import Box from 'modules/common/Box';
import Caret from 'modules/common/Caret';
import NewDesignButton from 'modules/common/NewDesignButton';
import ProgressBar from 'modules/common/ProgressBar';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {compose, withProps, withStateHandlers} from 'recompose';
import StepLines from './StepLines';

const Stage = ({
  authenticated,
  id,
  name,
  description,
  className,
  isDegree,
  slug,
  steps,
  stepsProgress,
  currentStepId,
  courseSlug,
  stageProgress,
  percentage,
  nextStep,
  expanded,
  toggleAccordion,
  trackSlug,
  cyStage,
  subscription,
}) => {

  const url = nextStep.isLocked && subscription !== 'ACTIVE' ?
    '/subscription' : `/${isDegree ? 'degrees/' : ''}${trackSlug}/${courseSlug}/${slug}/${nextStep.slug}`;

  return (
    <Box className={className} noPadding>
      <div className="f5">
        <div>
          <div className="pv3 ph4 cf">
            <h2
              className="fr f5 w-80-ns w-100-m w-100 mt1 tr-ns tc mb2-ns mb3"
              data-cy={cyStage}
            >
              {name}
            </h2>
            <div className="fl tl w-20-ns w-100">
              <ProgressBar percentage={percentage} />
            </div>
          </div>
          <div className="pv3 pr4 pl5">
            <p
              className="normal"
              dangerouslySetInnerHTML={{__html: description}}
            />
          </div>
        </div>
        {expanded && (
          <StepLines
            steps={steps}
            stageID={id}
            stageSlug={slug}
            isDegree={isDegree}
            trackSlug={trackSlug}
            currentStepId={currentStepId}
            courseSlug={courseSlug}
            authenticated={authenticated}
            stepsProgress={stepsProgress}
            stageProgress={stageProgress}
            subscription={subscription}
          />
        )}
        <div>
          <div className="ph4 pv3 bg-near-white flex items-center justify-between flex-row-ns flex-column lh-copy">
            <button
              aria-label="Course details"
              className="dib pv3 mb2 mb0-ns pv1-ns blue bn bg-transparent outline-0 pointer"
              onClick={toggleAccordion}
              type="button"
            >
              <FormattedMessage id="course_details" />
              <Caret side={expanded ? 'top' : 'bottom'} />
            </button>
            <div className="flex items-center flex-row-ns flex-column">
              <NewDesignButton
                className="pv2 ph4 f6 bg-animate bg-blue hover-bg-dark-blue mb0-ns mb2"
                link={url}
                state = {{ fromLink: window.location.pathname }}
              >
                <div className="ph3">
                  {stageProgress === 'completed' ? (
                    <FormattedMessage id="restart_course" />
                  ) : (
                    <FormattedMessage
                      id={
                        stageProgress === 'started'
                          ? 'course_continue'
                          : 'start_course'
                      }
                    />
                  )}
                </div>
              </NewDesignButton>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

Stage.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  className: PropTypes.string,
  courseSlug: PropTypes.string.isRequired,
  currentStepId: PropTypes.number.isRequired,
  cyStage: PropTypes.string,
  description: PropTypes.string.isRequired,
  isDegree: PropTypes.bool,
  expanded: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  nextStep: PropTypes.object.isRequired,
  percentage: PropTypes.number.isRequired,
  steps: PropTypes.array.isRequired,
  slug: PropTypes.string.isRequired,
  stageProgress: PropTypes.string,
  stepsProgress: PropTypes.object.isRequired,
  toggleAccordion: PropTypes.func.isRequired,
  trackSlug: PropTypes.string,
};

const enhance = compose(
  injectIntl,
  withProps(({steps, stepsProgress}) => {
    const completedSteps = steps.filter(
      (step) => stepsProgress[step.id] === 'completed',
    );
    const percentage =
      steps && steps.length
        ? Math.round((completedSteps.length * 1e2) / steps.length)
        : 0;
    const nextStep =
      steps.find(({id}) => stepsProgress[id] !== 'completed') || steps[0];
    return {
      percentage,
      nextStep: nextStep || {},
    };
  }),
  withStateHandlers(
    () => ({
      expanded: false,
    }),
    {
      toggleAccordion: ({expanded}) => () => ({expanded: !expanded}),
    },
  ),
);

export default enhance(Stage);
